/** HALOUL ȘI MÂNUȚA: ce a luat asistentul în primire, desenat peste planșă.
 *
 *  E SELECȚIA, NU O DECORAȚIE. Când asistentul mută o masă, primul lucru care
 *  trebuie să se vadă e CARE masă — la fel ca atunci când o apucă cineva cu
 *  mâna. De-aia haloul poartă chiar culoarea selecției: nu e o a doua limbă de
 *  învățat, e aceeași.
 *
 *  DE CE NU FOLOSEȘTE `ales`. Selecția editorului e UN lucru — cel al cărui
 *  panou se citește la dreapta, cu apucătoarele lui. O cerere ca «mută toți
 *  pereții interiori» atinge unsprezece, și niciunul nu e «cel ales»:
 *  apucătoarele a unsprezece ziduri peste desen ar fi fost o încurcătură, nu o
 *  arătare. Haloul spune mulțimea; selecția rămâne a mâinii, iar editorul o
 *  pune pe elementul rămas, la sfârșit, dacă e chiar unul singur.
 *
 *  MÂNUȚA arată CUM: se apropie de obiect, îl apucă, îl trage, îl lasă. Nu e
 *  cochetărie — e singura formă în care o schimbare venită din chat seamănă cu
 *  una făcută din interfață, adică e de înțeles fără să citești ce scrie în
 *  panou. Se vede doar când actul mută UN obiect: o mână care sare din zece în
 *  zece obiecte nu mai seamănă cu o mână, deci la derulare rapidă lipsește.
 *
 *  FANTOMELE CELOR ȘTERSE se desenează din planul VECHI, cât ține alegerea:
 *  altfel o ștergere n-ar avea nimic de arătat, obiectul fiind deja plecat din
 *  document când ajunge aici.
 */
import { SEL, SEL_MOALE, TUS_CULOARE } from "../Desen";
import { conturulTintei } from "./aratare";
import type { Cadru, Tinta } from "./aratare";
import type { PlanTronson, Pt } from "../doc";

const cale = (inel: readonly Pt[]) =>
  `${inel.map((p, i) => `${i ? "L" : "M"}${p[0]} ${p[1]}`).join(" ")}Z`;

export default function Arata({ plan, vechi, tinte, cadru, k }: {
  /** Planul DESENAT acum — cu alunecarea în el, deci haloul merge cu obiectul. */
  plan: PlanTronson;
  /** Planul de dinaintea schimbării, pentru fantomele celor șterse. */
  vechi: PlanTronson;
  tinte: readonly Tinta[];
  cadru: Cadru;
  /** Pixeli pe metru: mânuța se desenează la mărimea ecranului, nu a planului. */
  k: number;
}) {
  if (cadru.opac <= 0.02 && !cadru.mana) return null;
  return (
    <>
      <g pointerEvents="none" opacity={cadru.opac}>
        {tinte.map((t, i) => {
          const sters = t.fel === "sters";
          if (sters && !cadru.cuSterse) return null;
          const inel = conturulTintei(sters ? vechi : plan, t.ales);
          if (!inel?.length) return null;
          const d = cale(inel);
          return (
            <g key={i}>
              {/* Aura moale, întâi: o linie groasă și stinsă sub cea apăsată dă
                  adâncime fără să îngroașe conturul obiectului. */}
              <path d={d} fill="none" stroke={SEL_MOALE} strokeWidth={9}
                    strokeLinejoin="round" opacity={0.45}
                    vectorEffect="non-scaling-stroke" />
              <path d={d} fill={SEL} fillOpacity={sters ? 0.16 : 0.09}
                    stroke={SEL} strokeWidth={2} strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke" />
            </g>
          );
        })}
      </g>
      {cadru.mana && <Manuta p={cadru.mana.p} apasa={cadru.mana.apasa} k={k} />}
    </>
  );
}

/** MÂNUȚA, la mărimea ecranului. Desenată din două bucăți — palma și arătătorul
 *  —, cu vârful degetului chiar în punctul pe care îl arată: acolo e apucat
 *  obiectul, deci acolo trebuie să fie și mâna.
 *
 *  ALBĂ CU CONTUR DE TUȘ, ca orice cursor: o mână colorată s-ar fi pierdut
 *  peste haloul albastru sau peste o pardoseală închisă. */
function Manuta({ p, apasa, k }: { p: Pt; apasa: boolean; k: number }) {
  // Mărimea unui cursor de sistem: ~30 px de la vârful degetului la podul
  // palmei. Mai mică se pierde peste hașura zidurilor, mai mare acoperă chiar
  // obiectul pe care îl arată.
  const s = 1.4 / k;
  return (
    <g pointerEvents="none"
       transform={`translate(${p[0]} ${p[1]}) scale(${s})`}>
      {/* APĂSAREA se vede ca o pată care strânge sub deget — semnul că
          obiectul e prins, nu doar atins. SUB mânuță, nu în jurul ei: un inel
          mai mare decât mâna arăta ca o bulă, nu ca o apăsare. */}
      {apasa && (
        <circle cx={0} cy={0} r={7} fill={SEL} fillOpacity={0.22} />
      )}
      <g transform={`translate(-3.5 -1) rotate(${apasa ? 8 : 0} 3.5 1)`}>
        {/* Conturul alb de dedesubt ține mânuța citibilă și peste hașura
            zidului, și peste haloul albastru: e un cursor, nu un desen al
            planșei. */}
        <path d={CALEA_MAINII} fill="none" stroke="#fff" strokeWidth={4}
              strokeLinejoin="round" strokeLinecap="round" />
        <path d="M1 11 h9 a5 5 0 0 1 5 5 v5 a6 6 0 0 1 -6 6 h-4
                 a5 5 0 0 1 -5 -5 z"
              fill="#fff" stroke={TUS_CULOARE} strokeWidth={1.4}
              strokeLinejoin="round" />
        <path d={apasa ? "M1 12 v-5 a2.5 2.5 0 0 1 5 0 v5 z"
                       : "M1 12 v-9 a2.5 2.5 0 0 1 5 0 v9 z"}
              fill="#fff" stroke={TUS_CULOARE} strokeWidth={1.4}
              strokeLinejoin="round" />
      </g>
    </g>
  );
}

/** Silueta mânuții, dintr-o bucată — numai pentru conturul alb de dedesubt. */
const CALEA_MAINII = "M1 6 a2.5 2.5 0 0 1 5 0 v5 h4 a5 5 0 0 1 5 5 v5 "
  + "a6 6 0 0 1 -6 6 h-4 a5 5 0 0 1 -5 -5 z";
